import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { ENTITY_MODAL_TYPES } from './entity-modal.constants';
import { useTranslation } from 'react-i18next';
import { FieldLabel, ModalInput, Pill, FieldCard, Section, TagList } from './EntityModalComponents';
import type { EntityFormProps } from './entity-modal.types';

const accent = ENTITY_MODAL_TYPES.contact.accent;

export function ContactForm({
  isEdit,
  formData,
  onChange,
  tags,
  onAddTag,
  onRemoveTag,
}: EntityFormProps) {
  const { t } = useTranslation();
  const initial = (formData.name || '').charAt(0).toUpperCase();
  const [isAddingCustomField, setIsAddingCustomField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');

  const customFields: Array<{ label: string; value: string }> = formData.customFields || [];

  const handleAddCustomField = () => {
    if (!newFieldLabel.trim()) return;
    onChange('customFields', [...customFields, { label: newFieldLabel.trim(), value: '' }]);
    setNewFieldLabel('');
    setIsAddingCustomField(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3.5">
        <div
          className="w-[52px] h-[52px] rounded-xl flex items-center justify-center shrink-0 cursor-pointer"
          style={{
            background: `linear-gradient(135deg, ${accent}26, ${accent}40)`,
            border: `1px dashed ${accent}59`,
            color: accent,
          }}
        >
          {isEdit && initial ? (
            <span className="text-xl font-semibold">{initial}</span>
          ) : (
            <Plus size={16} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            type="text"
            required
            placeholder={t('entity.contact_name_placeholder')}
            value={formData.name || ''}
            onChange={(e) => onChange('name', e.target.value)}
            className="w-full bg-transparent border-none text-lg font-semibold text-zinc-100 outline-none placeholder:text-zinc-600 tracking-tight"
          />
          <input
            type="text"
            placeholder={t('entity.contact_role_placeholder')}
            value={formData.role || ''}
            onChange={(e) => onChange('role', e.target.value)}
            className="w-full bg-transparent border-none text-[13px] text-zinc-400 outline-none placeholder:text-zinc-600 mt-0.5"
          />
        </div>
      </div>

      <div className="flex gap-1.5">
        <Pill
          label={t('entity.contact_personal')}
          color="#a1a1aa"
          active={formData.contactType !== 'pro'}
          onClick={() => onChange('contactType', 'perso')}
        />
        <Pill
          label={t('entity.contact_professional')}
          color="#8b5cf6"
          active={formData.contactType === 'pro'}
          onClick={() => onChange('contactType', 'pro')}
        />
      </div>

      <FieldCard accent={accent}>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <FieldLabel>{t('entity.contact_email')}</FieldLabel>
            <ModalInput
              type="email"
              placeholder="exemple@email.com"
              value={formData.email || ''}
              onChange={(val) => onChange('email', val)}
            />
          </div>
          <div>
            <FieldLabel>{t('entity.contact_phone')}</FieldLabel>
            <ModalInput
              type="tel"
              placeholder="+33 6 00 00 00 00"
              value={formData.phone || ''}
              onChange={(val) => onChange('phone', val)}
            />
          </div>
        </div>

        {formData.contactType === 'pro' && (
          <>
            <div className="grid grid-cols-2 gap-2.5 mt-2.5">
              <div>
                <FieldLabel>{t('entity.contact_website')}</FieldLabel>
                <ModalInput
                  placeholder="https://monsite.com"
                  value={formData.website || ''}
                  onChange={(val) => onChange('website', val)}
                />
              </div>
              <div>
                <FieldLabel>{t('entity.contact_linkedin')}</FieldLabel>
                <ModalInput
                  placeholder="linkedin.com/in/prenom-nom"
                  value={formData.linkedinUrl || ''}
                  onChange={(val) => onChange('linkedinUrl', val)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 mt-2.5">
              <div>
                <FieldLabel>{t('entity.contact_siret')}</FieldLabel>
                <ModalInput
                  placeholder="123 456 789 00012"
                  value={formData.siret || ''}
                  onChange={(val) => onChange('siret', val)}
                />
              </div>
              <div>
                <FieldLabel>{t('entity.contact_tva')}</FieldLabel>
                <ModalInput
                  placeholder="FR 00 123456789"
                  value={formData.tvaNumber || ''}
                  onChange={(val) => onChange('tvaNumber', val)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 mt-2.5">
              <div>
                <FieldLabel>{t('entity.contact_sector')}</FieldLabel>
                <ModalInput
                  placeholder={t('entity.contact_sector_placeholder')}
                  value={formData.sector || ''}
                  onChange={(val) => onChange('sector', val)}
                />
              </div>
              <div>
                <FieldLabel>{t('entity.contact_address')}</FieldLabel>
                <ModalInput
                  placeholder={t('entity.contact_address_placeholder')}
                  value={formData.professionalAddress || ''}
                  onChange={(val) => onChange('professionalAddress', val)}
                />
              </div>
            </div>
          </>
        )}

        {/* Custom fields */}
        {customFields.length > 0 && (
          <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-zinc-700/25">
            {customFields.map((cf, index) => (
              <div key={index} className="flex items-end gap-2">
                <div className="flex-1">
                  <FieldLabel>{cf.label}</FieldLabel>
                  <ModalInput
                    placeholder={t('entity.contact_custom_field_placeholder')}
                    value={cf.value}
                    onChange={(val) =>
                      onChange(
                        'customFields',
                        customFields.map((f, i) => (i === index ? { ...f, value: val } : f))
                      )
                    }
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    onChange(
                      'customFields',
                      customFields.filter((_, i) => i !== index)
                    )
                  }
                  className="p-2 text-zinc-600 hover:text-red-400 transition-colors cursor-pointer shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-zinc-700/25">
          {isAddingCustomField ? (
            <div className="flex items-center gap-2">
              <ModalInput
                placeholder={t('entity.contact_custom_field_name')}
                value={newFieldLabel}
                onChange={(val) => setNewFieldLabel(val)}
              />
              <button
                type="button"
                onClick={handleAddCustomField}
                disabled={!newFieldLabel.trim()}
                className="px-3 py-2 rounded-lg text-[11px] font-medium transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: `${accent}1e`, color: accent }}
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAddingCustomField(false);
                  setNewFieldLabel('');
                }}
                className="p-2 text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsAddingCustomField(true)}
              className="flex items-center gap-1.5 text-[11.5px] font-medium transition-colors cursor-pointer"
              style={{ color: accent }}
            >
              <Plus size={13} />
              {t('entity.contact_add_field')}
            </button>
          )}
        </div>
      </FieldCard>

      <Section title="Tags" defaultOpen={false} accent={accent}>
        <TagList tags={tags} accent={accent} onAdd={onAddTag} onRemove={onRemoveTag} />
      </Section>
    </div>
  );
}
